import React, { useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { getToken } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Unlink, AlertTriangle, ShieldCheck } from "lucide-react";

export const LoginSecuritySection = () => {
  const [problems, setProblems] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [linked, setLinked] = useState([]);
  const [tab, setTab] = useState("problems");

  const load = () => {
    adminApi.loginProblems().then(setProblems);
    adminApi.loginAttempts().then(setAttempts);
    adminApi.googleLinked().then(setLinked);
  };
  useEffect(() => { load(); }, []);

  const downloadCsv = async () => {
    try {
      const res = await fetch(adminApi.loginProblemsCsvUrl(), { headers: { Authorization: `Bearer ${getToken()}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "login-problems.csv";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Could not download CSV"); }
  };
  const unlink = async (m) => {
    if (!window.confirm(`Unlink the Google account from ${m.name_en}?`)) return;
    try { await adminApi.unlinkGoogle(m.id); toast.success("Google account unlinked"); load(); }
    catch { toast.error("Could not unlink"); }
  };

  const Tab = ({ id, label, count }) => (
    <button onClick={() => setTab(id)} data-testid={`login-tab-${id}`}
      className={`rounded-full px-3 py-1.5 text-xs font-medium ${tab === id ? "bg-gmoaa-green text-white" : "border border-gmoaa-border-subtle bg-white text-gmoaa-text-dark"}`}>
      {label}{count != null ? ` (${count})` : ""}
    </button>
  );

  return (
    <div className="space-y-4">
      <h2 className="font-serif text-2xl font-semibold text-gmoaa-green">Login problems &amp; activity</h2>
      <div className="flex flex-wrap gap-2">
        <Tab id="problems" label="Login problems" count={problems.length} />
        <Tab id="attempts" label="Login attempts" count={attempts.length} />
        <Tab id="linked" label="Google-linked" count={linked.length} />
      </div>

      {tab === "problems" && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="flex items-center gap-2 text-sm text-gmoaa-text-muted"><AlertTriangle size={15} className="text-gmoaa-saffron-hover" /> Records that cannot log in with Google/email — fix the email on the roster.</p>
            <Button size="sm" variant="outline" onClick={downloadCsv} data-testid="login-problems-csv"><Download size={14} className="mr-1" /> CSV</Button>
          </div>
          <div className="overflow-hidden rounded-xl border border-gmoaa-border-subtle bg-white">
            <div className="grid grid-cols-12 gap-2 border-b border-gmoaa-border-subtle bg-gmoaa-green-light px-3 py-2 text-xs font-semibold">
              <div className="col-span-3">Problem</div><div className="col-span-3">Name</div><div className="col-span-2">Mobile</div><div className="col-span-2">District</div><div className="col-span-2">Email</div>
            </div>
            {problems.map((p) => (
              <div key={p.id} className="grid grid-cols-12 gap-2 border-b border-gmoaa-border-subtle px-3 py-2 text-xs last:border-0" data-testid={`login-problem-${p.id}`}>
                <div className="col-span-3"><span className={`rounded px-1.5 py-0.5 ${p.problem === "no_email" ? "bg-gmoaa-red-bg text-gmoaa-red" : "bg-gmoaa-saffron-light text-gmoaa-saffron-hover"}`}>{p.problem === "no_email" ? "No email" : "Duplicate email"}</span></div>
                <div className="col-span-3">{p.name_en}</div><div className="col-span-2">{p.mobile}</div><div className="col-span-2">{p.district}</div><div className="col-span-2 truncate">{p.email || "—"}</div>
              </div>
            ))}
            {problems.length === 0 && <p className="px-3 py-6 text-center text-sm text-gmoaa-text-muted">No login problems 🎉</p>}
          </div>
        </div>
      )}

      {tab === "attempts" && (
        <div className="overflow-hidden rounded-xl border border-gmoaa-border-subtle bg-white">
          <div className="grid grid-cols-12 gap-2 border-b border-gmoaa-border-subtle bg-gmoaa-green-light px-3 py-2 text-xs font-semibold">
            <div className="col-span-4">Time</div><div className="col-span-2">Method</div><div className="col-span-3">Email</div><div className="col-span-1">Result</div><div className="col-span-2">Reason</div>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {attempts.map((a) => (
              <div key={a.id} className="grid grid-cols-12 gap-2 border-b border-gmoaa-border-subtle px-3 py-1.5 text-xs last:border-0">
                <div className="col-span-4">{new Date(a.at).toLocaleString()}</div>
                <div className="col-span-2"><span className={`rounded px-1.5 py-0.5 ${a.method === "google" ? "bg-gmoaa-green-light text-gmoaa-green" : "bg-gmoaa-surface"}`}>{a.method}</span></div>
                <div className="col-span-3">{a.email_masked}</div>
                <div className="col-span-1">{a.success ? <span className="text-gmoaa-green">✓</span> : <span className="text-gmoaa-red">✕</span>}</div>
                <div className="col-span-2 truncate text-gmoaa-text-muted">{a.reason || ""}</div>
              </div>
            ))}
            {attempts.length === 0 && <p className="px-3 py-6 text-center text-sm text-gmoaa-text-muted">No attempts logged yet.</p>}
          </div>
        </div>
      )}

      {tab === "linked" && (
        <div className="overflow-hidden rounded-xl border border-gmoaa-border-subtle bg-white">
          <p className="flex items-center gap-2 border-b border-gmoaa-border-subtle px-3 py-2 text-sm text-gmoaa-text-muted"><ShieldCheck size={15} className="text-gmoaa-green" /> Members with a linked Google account.</p>
          {linked.map((m) => (
            <div key={m.id} className="flex items-center justify-between border-b border-gmoaa-border-subtle px-3 py-2 text-sm last:border-0" data-testid={`google-linked-${m.id}`}>
              <div><p className="font-medium">{m.name_en}</p><p className="text-xs text-gmoaa-text-muted">{m.email} · {m.district}</p></div>
              <Button size="sm" variant="ghost" className="text-gmoaa-red" onClick={() => unlink(m)} data-testid={`unlink-google-${m.id}`}><Unlink size={14} className="mr-1" /> Unlink</Button>
            </div>
          ))}
          {linked.length === 0 && <p className="px-3 py-6 text-center text-sm text-gmoaa-text-muted">No Google-linked members yet.</p>}
        </div>
      )}
    </div>
  );
};
